import { usePortal, type VocLibraryView } from "@app/portal-sdk";
import { Alert, DataTable, SectionCard, Stack, Typography } from "@app/ui-kit";
import { LoadingScreen } from "../../components/LoadingScreen";
import { useApiQuery } from "../../lib/useApiQuery";

/**
 * MNT-5: what the Voice of Customer library currently holds, read-only, so a portal admin can see what every client
 * is seeded from without cloning a repository. The file is the source; this reads it as shipped with the API. No
 * control of any kind: the registry says where it is maintained and what changing it costs, and a sentence that
 * tells the truth beats a control that lies (MNT-5 criterion 3).
 */
export const VoiceOfCustomerLibrary = () => {
  const { api } = usePortal();
  const q = useApiQuery<VocLibraryView>(() => api.request<VocLibraryView>("/admin/voice-of-customer/library"), [api]);
  if (q.loading && !q.data) return <LoadingScreen minHeight={160} />;
  if (q.error || !q.data) return <Alert severity="error">{q.error ?? "The library file could not be read."}</Alert>;
  const d = q.data;
  return (
    <Stack spacing={2}>
      <SectionCard eyebrow="As shipped" title={`${d.questions.length} questions in ${d.categories.length} categories`}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }} data-testid="library-source">Read from {d.file}, version {d.version}. {d.description}</Typography>
        <DataTable columns={[{ key: "category", label: "Category" }, { key: "key", label: "Key" }, { key: "text", label: "Question" }, { key: "type", label: "Type" }, { key: "guidance", label: "Guidance" }]}
          rows={d.questions.map((x) => ({
            category: x.category?.label ?? "—",
            key: <Typography variant="caption" sx={{ fontFamily: "monospace" }} data-testid={`voc-q-${x.questionKey}`}>{x.questionKey}</Typography>,
            text: x.text,
            type: x.type?.label ?? x.typeCode,
            guidance: <Typography variant="caption" color="text.secondary">{x.guidance ?? ""}</Typography>
          }))} />
      </SectionCard>
      {d.rules.length > 0 && (
        <SectionCard eyebrow="The file's own rules" title="How the seed behaves">
          <Stack spacing={0.5}>{d.rules.map((r, i) => <Typography key={i} variant="body2">{r}</Typography>)}</Stack>
        </SectionCard>
      )}
    </Stack>
  );
};
